import { TransactionSplit } from '../types';
import { parseQBLinkTargetId, splitToLinkTargetId } from './qbDealLinkOptions';

export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export type SplitSavePlan =
  | { kind: 'error'; message: string }
  | { kind: 'single'; dealId: string | null; dealTitle?: string; projectPhase?: import('../types').ProjectPhase }
  | { kind: 'split'; splits: TransactionSplit[] };

/**
 * Validate a split draft and decide whether to persist as a single-deal link
 * or a multi-slice split (same rules as QB Transactions modal).
 */
export function planTransactionSplitSave(
  txnTotal: number,
  draft: TransactionSplit[],
  dealTitleById: Record<string, string>,
): SplitSavePlan {
  const total = round2(txnTotal);
  const allocated = round2(draft.reduce((s, x) => s + (Number(x.amount) || 0), 0));
  const remainder = round2(total - allocated);
  if (remainder < -0.01) {
    return {
      kind: 'error',
      message: `Allocated $${allocated.toFixed(2)} is more than this transaction ($${total.toFixed(2)}).`,
    };
  }

  const cleaned = draft
    .filter(s => Number(s.amount) > 0)
    .map(s => {
      const parsed = parseQBLinkTargetId(s.dealId);
      return {
        dealId: parsed.dealId,
        projectPhase: parsed.projectPhase,
        amount: round2(Number(s.amount) || 0),
        ...(s.note ? { note: s.note } : {}),
      };
    })
    .filter(s => s.amount > 0);

  if (cleaned.length === 0) {
    return { kind: 'error', message: 'Add at least one slice with a positive amount.' };
  }
  if (!cleaned.some(s => s.dealId)) {
    return { kind: 'error', message: 'Link at least one slice to a project tracked here.' };
  }

  const withTitles = cleaned.map<TransactionSplit>(s => ({
    dealId: s.dealId,
    projectPhase: s.projectPhase,
    dealTitle: s.dealId
      ? dealTitleById[splitToLinkTargetId(s)] || (s.dealId ? dealTitleById[s.dealId] : undefined)
      : undefined,
    amount: s.amount,
    ...(s.note ? { note: s.note } : {}),
  }));

  if (withTitles.length === 1) {
    const slice = withTitles[0];
    const sliceAmt = round2(Number(slice.amount) || 0);
    if (Math.abs(total - sliceAmt) <= 0.01) {
      return {
        kind: 'single',
        dealId: slice.dealId,
        dealTitle: slice.dealTitle,
        projectPhase: slice.projectPhase,
      };
    }
  }

  return { kind: 'split', splits: withTitles };
}

export function seedSplitDraft(): TransactionSplit[] {
  return [
    { dealId: null, amount: 0 },
    { dealId: null, amount: 0 },
  ];
}
